import { useEffect, useRef } from 'react'

import type { RefObject } from 'react'

/**
 * This allows us to add a handler within a component
 * and execute the handler when the size of the browser window changes
 * @param handler
 */
export const useResize = (handler: EventListener) => {
	useEffect(() => {
		window.addEventListener('resize', handler)

		return () => {
			window.removeEventListener('resize', handler)
		}
	}, [handler])
}

/**
 * Allows us to watch a specific HTML element for size changes
 * and fire off an action. This is different from the resize event
 * fired by the browser when the window is resized
 *
 * Example usage:
 * const containerRef = useRef<HTMLElement>();
 * useResizeObserver(containerRef, (dimensions) => {
 *      console.log(`New container dimensions: ${dimensions}`);
 * });
 *
 * @param ref
 * @param callback
 */
export const useResizeObserver = <T>(
	ref: RefObject<T>,
	callback: (dimensions?: DOMRectReadOnly) => void
) => {
	useEffect(() => {
		if (ref.current) {
			const obs = new ResizeObserver((entries) => {
				entries.forEach((entry) => {
					callback(entry.contentRect)
				})
			})
			obs.observe(ref.current as unknown as HTMLElement)

			return () => obs.disconnect()
		}
	}, [ref, callback])
}

export const useMapContainerResizeWithRef = (onResize: () => void) => {
	const containerRef = useRef<HTMLElement>(null)
	useResizeObserver(containerRef, onResize)

	return containerRef
}
